import { PERSONAL_HUB } from "@/components/courses/personalHubTokens";
import {
  BodyText,
  CaptionText,
  CardTitle,
  ContentTitle,
} from "@/components/ui/Typography";
import { BRAND_COLORS as C } from "@/lib/brandColors";
import { useIsMobile } from "@/lib/useDeviceType";
import { Ionicons } from "@expo/vector-icons";
import React from "react";
import { Platform, Pressable } from "react-native";
import { XStack, YStack } from "tamagui";

const HAIRLINE = "rgba(0,0,0,0.05)";

/** Same horizontal padding the "For you" hub panel uses. */
function usePanelPad() {
  const isMobile = useIsMobile();
  return isMobile ? ("$3" as const) : ("$3.5" as const);
}

/**
 * Account section — same panel language as the "For you" hub panel.
 * Typography uses the shared roles only (no raw fontSize overrides).
 */
export function SettingsPanel({
  title,
  subtitle,
  footnote,
  children,
}: {
  title: string;
  subtitle?: string;
  footnote?: string;
  children: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  const px = usePanelPad();
  const rows = React.Children.toArray(children).filter(Boolean);

  return (
    <YStack w="100%" f={1}>
      <YStack
        w="100%"
        f={1}
        br={PERSONAL_HUB.panelRadius}
        borderWidth={1}
        borderColor={PERSONAL_HUB.panelBorder}
        bg={C.cardBg}
        overflow="hidden"
        boxShadow={PERSONAL_HUB.panelShadow}
      >
        <XStack
          px={px}
          pt={isMobile ? "$2.5" : "$3"}
          pb="$2"
          ai="center"
          gap="$2"
        >
          <YStack w={3} h={14} br={2} bg={C.accent} flexShrink={0} />
          <YStack f={1} miw={0} gap={2}>
            <CardTitle color={C.text} letterSpacing={-0.15} ta="left">
              {title}
            </CardTitle>
            {subtitle ? (
              <CaptionText color={C.muted} ta="left">
                {subtitle}
              </CaptionText>
            ) : null}
          </YStack>
        </XStack>

        {rows.map((row, i) => (
          <YStack key={i} borderTopWidth={1} borderColor={HAIRLINE}>
            {row}
          </YStack>
        ))}
      </YStack>

      {footnote ? (
        <CaptionText color={C.muted} mt="$2" ml="$1" ta="left">
          {footnote}
        </CaptionText>
      ) : null}
    </YStack>
  );
}

type SettingsRowProps = {
  label: string;
  value?: string | null;
  hint?: string | null;
  onPress?: () => void;
  /** Leaves the app — external glyph instead of a chevron. */
  external?: boolean;
  /** Overrides the trailing glyph (chevron / external). */
  icon?: keyof typeof Ionicons.glyphMap;
  destructive?: boolean;
};

export function SettingsRow({
  label,
  value,
  hint,
  onPress,
  external,
  icon,
  destructive,
}: SettingsRowProps) {
  const px = usePanelPad();
  const [hovered, setHovered] = React.useState(false);
  const interactive = Boolean(onPress);
  const tint = destructive ? C.error : C.text;

  const body = (
    <XStack
      ai="center"
      gap="$3"
      px={px}
      py="$3"
      minHeight={52}
      bg={interactive && hovered ? PERSONAL_HUB.tileBg : "transparent"}
      {...(Platform.OS === "web" && interactive
        ? ({
            cursor: "pointer",
            transition: "background-color 140ms ease",
          } as object)
        : {})}
    >
      {/* RN-web maps accessibilityRole="button" → <button>, which centers text. */}
      <YStack f={1} miw={0} gap={2}>
        <BodyText
          color={tint}
          fontWeight={destructive ? "600" : "400"}
          ta="left"
        >
          {label}
        </BodyText>
        {hint ? (
          <CaptionText color={C.muted} ta="left">
            {hint}
          </CaptionText>
        ) : null}
      </YStack>

      {value ? (
        <CaptionText
          color={C.muted}
          numberOfLines={1}
          flexShrink={1}
          maw="45%"
          ta="right"
        >
          {value}
        </CaptionText>
      ) : null}

      {interactive ? (
        <Ionicons
          name={icon ?? (external ? "open-outline" : "chevron-forward")}
          size={icon || external ? 16 : 18}
          color={destructive ? C.error : C.muted}
        />
      ) : null}
    </XStack>
  );

  if (!interactive) return body;

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
    >
      {body}
    </Pressable>
  );
}

/**
 * Full-width identity hero — sits above the action panels so name/email/plan
 * are not repeated as list rows (Spotify / Linear / Notion pattern).
 */
export function SettingsHero({
  name,
  email,
  badge,
  onEditName,
}: {
  name?: string | null;
  email?: string | null;
  badge?: string | null;
  onEditName?: () => void;
}) {
  const isMobile = useIsMobile();
  const [hovered, setHovered] = React.useState(false);
  const display = name?.trim() || null;
  const initials = (display || email || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  const avatarSize = isMobile ? 52 : 56;

  return (
    <YStack
      w="100%"
      br={PERSONAL_HUB.panelRadius}
      borderWidth={1}
      borderColor={PERSONAL_HUB.panelBorder}
      bg={C.cardBg}
      overflow="hidden"
      boxShadow={PERSONAL_HUB.panelShadow}
    >
      <XStack
        ai="center"
        gap="$3"
        px={isMobile ? "$3.5" : "$4"}
        py={isMobile ? "$3.5" : "$4"}
        bg={PERSONAL_HUB.emphasisBg}
      >
        <YStack
          w={avatarSize}
          h={avatarSize}
          br={999}
          ai="center"
          jc="center"
          bg={PERSONAL_HUB.accentSoft}
          flexShrink={0}
        >
          <CardTitle color={C.accent}>{initials}</CardTitle>
        </YStack>

        <YStack f={1} miw={0} gap={2}>
          <ContentTitle color={C.text} numberOfLines={1} ta="left">
            {display ?? "Add your name"}
          </ContentTitle>
          {email ? (
            <CaptionText color={C.muted} numberOfLines={1} ta="left">
              {email}
            </CaptionText>
          ) : null}
          {onEditName ? (
            <Pressable
              onPress={onEditName}
              onHoverIn={() => setHovered(true)}
              onHoverOut={() => setHovered(false)}
              accessibilityRole="button"
              hitSlop={6}
              style={
                Platform.OS === "web" ? { alignSelf: "flex-start" } : undefined
              }
            >
              <XStack
                ai="center"
                gap={4}
                mt={2}
                opacity={hovered ? 0.75 : 1}
              >
                <CaptionText color={C.accent} fontWeight="600" ta="left">
                  {display ? "Edit details" : "Add your name"}
                </CaptionText>
                <Ionicons name="chevron-forward" size={12} color={C.accent} />
              </XStack>
            </Pressable>
          ) : null}
        </YStack>

        {badge ? (
          <XStack
            bg={C.cardBg}
            borderWidth={1}
            borderColor={PERSONAL_HUB.accentSoft}
            px="$2.5"
            py="$1"
            br={999}
            flexShrink={0}
          >
            <CaptionText color={C.accent} fontWeight="700">
              {badge}
            </CaptionText>
          </XStack>
        ) : null}
      </XStack>
    </YStack>
  );
}

/** Shared form field padding inside a SettingsPanel. */
export function SettingsPanelBody({
  children,
  gap = "$3",
}: {
  children: React.ReactNode;
  gap?: string | number;
}) {
  const px = usePanelPad();
  return (
    <YStack px={px} py="$3" gap={gap}>
      {children}
    </YStack>
  );
}
